import { css } from '@emotion/css';
import { Colors } from '../../../shared/styles/colors';
import { Layout } from '../../../shared/components/layout/Layout';
import { User } from '../../../shared/types/server-types';
import { h1Style, valueStyle } from '../../../shared/styles/text';
import {
  Button,
  ButtonSizeEnum,
  ButtonVariantEnum,
} from '../../../shared/components/Button';
import { PlusCircleIcon } from '@heroicons/react/24/solid';
import { useState } from 'react';
import { ChevronLeft } from '../../../shared/assets/icons';
import { useNavigator } from '../../../routing/navigator';
import 'react-toastify/ReactToastify.css';
import { Modal } from '../vehicle-page/Modal';
import { NewEmployeeForm } from './NewEmployeeForm';
import { EditEmployeeForm } from './EditEmployeeForm';
import { useGetEmployees } from '../../../shared/services/api/user';
import { Loader } from '../../../shared/components/Loader';
import { BOTTOM_NAV_SAFE_HEIGHT } from '../../../shared/styles/css-values';
import { Motion, Radius, unstyledButton } from '../../../shared/styles/tokens';

export const PersonnelPage = (): JSX.Element => {
  const navigator = useNavigator();
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [modalContent, setModalContent] = useState<JSX.Element>(<></>);

  const { data: employees, isError, isLoading } = useGetEmployees();

  const handleModal = (content: JSX.Element) => {
    setModalContent(content);
    setModalVisible(true);
  };

  if (isLoading || !employees) {
    return <Loader />;
  }
  if (isError) {
    return <p className={errorStyle}>Something went wrong</p>;
  }

  return (
    <Layout.Content className={vehiclePageStyle}>
      <Button
        startIcon={<ChevronLeft />}
        variant={ButtonVariantEnum.TEXT}
        size={ButtonSizeEnum.LARGE}
        text={`account`}
        className={backButtonStyle}
        onClick={navigator.toPrevious}
      />
      <div className={vehiclePageHeaderStyle}>
        <h1>Personnel</h1>
        <span>
          <Button
            size={ButtonSizeEnum.MEDIUM}
            variant={ButtonVariantEnum.TEXT}
            text={'new'}
            startIcon={<PlusCircleIcon className={iconStyle} />}
            onClick={() =>
              handleModal(<NewEmployeeForm setModalVisible={setModalVisible} />)
            }
          />
        </span>
      </div>
      <ul className={vehiclesListStyle}>
        {employees?.map((user: User, index: number) => (
          <li key={index}>
            <button
              type="button"
              className={listItemButtonStyle}
              onClick={() =>
                handleModal(
                  <EditEmployeeForm
                    user={user}
                    setModalVisible={setModalVisible}
                  />,
                )
              }
            >
              <div className={listItemTextStyle}>
                <span className={nameStyle(user)}>
                  {user.name} {!user.verified && '(invited)'}
                </span>
                {user.email && (
                  <span className={infoStyle(user)}>{user.email}</span>
                )}
                {user.username && (
                  <span className={infoStyle(user)}>{user.username}</span>
                )}
              </div>
            </button>
          </li>
        ))}
      </ul>
      {/*
          This conditional rendering is needed because modal component uses
          custom hook that adds event listener when mounted,
          this produces a sideffect where modal can't be opened
       */}
      {modalVisible && (
        <Modal visible={modalVisible} setVisible={setModalVisible}>
          {modalContent}
        </Modal>
      )}
    </Layout.Content>
  );
};

const vehiclePageStyle = css`
  padding-top: 0.625rem;
  padding-bottom: calc(${BOTTOM_NAV_SAFE_HEIGHT} + 2rem);
`;

const errorStyle = css`
  padding: 2rem 1.5rem;
  color: ${Colors.TEXT_SECONDARY};
`;

const vehiclePageHeaderStyle = css`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  h1 {
    ${h1Style}
    margin: 0.5rem 0 1.5rem;
  }
`;

const vehiclesListStyle = css`
  list-style: none;
  display: flex;
  flex-direction: column;
  border: 1px solid ${Colors.BORDER};
  border-radius: ${Radius.lg};
  overflow: hidden;

  > li + li {
    border-top: 1px solid ${Colors.BORDER};
  }
`;

const listItemButtonStyle = css`
  ${unstyledButton}
  ${valueStyle}
  width: 100%;
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  transition: background-color ${Motion.fast};

  @media (hover: hover) {
    &:hover {
      background: ${Colors.SURFACE};
    }
  }

  &:focus-visible {
    outline: 2px solid ${Colors.CAPRI_BLUE};
    outline-offset: -2px;
  }
`;

const listItemTextStyle = css`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const nameStyle = (user: User) => css`
  ${valueStyle}
  margin-bottom: 0.25rem;
  font-weight: bold;
  overflow-wrap: anywhere;
  font-style: ${!user.verified ? 'italic' : 'normal'};
`;

const infoStyle = (user: User) => css`
  ${valueStyle}
  color: ${Colors.TEXT_SECONDARY};
  overflow-wrap: anywhere;
  font-style: ${!user.verified ? 'italic' : 'normal'};
`;

const iconStyle = css`
  width: 24px;
  height: 24px;
  color: ${Colors.CAPRI_BLUE};
`;

const backButtonStyle = css`
  padding-top: 0rem;
`;
